/**
 * Design Check home: hero upload dropzone + the client's previous analyses.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DesignCheckLayout from './DesignCheckLayout';
import NavTiles from '../../components/design-check/NavTiles';
import './DesignCheck.css';

const CUSTOM_CELL_ID = 'custom';
const CUSTOM_MM_MIN = 100;
const CUSTOM_MM_MAX = 20000;

function DesignCheckHome() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [jobs, setJobs] = useState([]);
  const [presets, setPresets] = useState([]);
  const [cellPresetId, setCellPresetId] = useState('');
  const [customMm, setCustomMm] = useState({ x: 2500, y: 2000, z: 1500 });
  const [materials, setMaterials] = useState([]);
  const [material, setMaterial] = useState('');
  const [printModes, setPrintModes] = useState([]);
  const [printMode, setPrintMode] = useState('auto');
  const [perimeterBeads, setPerimeterBeads] = useState(2);
  const [infillPct, setInfillPct] = useState(20);
  const [loadDirection, setLoadDirection] = useState('none');
  const [dragover, setDragover] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    fetch('/api/design-check/presets')
      .then((r) => r.json())
      .then((d) => {
        if (!d.success) throw new Error(d.error || 'Presets unavailable');
        setPresets(d.presets);
        const def = d.presets.find((p) => p.default);
        if (def) setCellPresetId(def.id);
        setMaterials(d.materials || []);
        const defMat = (d.materials || []).find((m) => m.default);
        if (defMat) setMaterial(defMat.key);
        setPrintModes(d.printModes || []);
      })
      .catch(() => setError('Could not load the upload options — please refresh the page.'));
    refreshJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshJobs = useCallback(() => {
    fetch('/api/design-check/jobs', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => {
        if (!d.success) throw new Error(d.error || 'Job list unavailable');
        setJobs(d.jobs || []);
      })
      .catch(() => setError('Could not load your previous analyses — please refresh the page.'));
  }, [token]);

  const handleFile = useCallback(async (file) => {
    setError('');
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith('.step') && !name.endsWith('.stp')) {
      setError('Please choose a .step or .stp file — that\'s the format your CAD package exports for manufacturing.');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError('Files up to 50MB are supported. Try exporting with a coarser tessellation or splitting the assembly.');
      return;
    }
    if (cellPresetId === CUSTOM_CELL_ID) {
      const bad = ['x', 'y', 'z'].find((axis) => {
        const v = Number(customMm[axis]);
        return !Number.isFinite(v) || v < CUSTOM_MM_MIN || v > CUSTOM_MM_MAX;
      });
      if (bad) {
        setError(`Custom bed size: ${bad.toUpperCase()} must be between ${CUSTOM_MM_MIN} and ${CUSTOM_MM_MAX} mm.`);
        return;
      }
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('stepFile', file);
      if (cellPresetId === CUSTOM_CELL_ID) {
        form.append('customEnvelopeMm', JSON.stringify({
          x: Number(customMm.x), y: Number(customMm.y), z: Number(customMm.z)
        }));
      } else if (cellPresetId) {
        form.append('cellPresetId', cellPresetId);
      }
      if (material) form.append('material', material);
      form.append('printMode', printMode);
      if (printMode === 'shell' || printMode === 'auto') {
        form.append('perimeterBeads', String(perimeterBeads));
        form.append('infillPct', String(infillPct));
      }
      if (loadDirection !== 'none') form.append('loadDirection', loadDirection);
      const res = await fetch('/api/design-check/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Upload failed');
      navigate(`/design-check/analysis/${data.jobId}`);
    } catch (err) {
      setError(err.message);
      setUploading(false);
    }
  }, [cellPresetId, customMm, material, printMode, perimeterBeads, infillPct, loadDirection, navigate, token]);

  const statusLabel = { queued: 'Queued', processing: 'Analysing', done: 'Ready', error: 'Failed' };

  const deleteJob = async (e, job) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${job.fileName}" and its report? This cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/design-check/jobs/${job.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Delete failed');
      refreshJobs();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <DesignCheckLayout>
      <div className="dc-hero">
        <h1>Will your part print?</h1>
        <p>
          Upload a STEP file and get an instant design-for-manufacture check for
          large-format robotic pellet printing — best orientation, layer height,
          bead width and expert advice from Bob, our AI print engineer.
        </p>
      </div>

      <NavTiles />

      {error && <div className="dc-error-banner" style={{ maxWidth: 720, margin: '0 auto 16px' }}>{error}</div>}

      <div
        className={`dc-dropzone ${dragover ? 'dragover' : ''}`}
        onClick={() => !uploading && fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragover(true); }}
        onDragLeave={() => setDragover(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragover(false);
          if (!uploading) handleFile(e.dataTransfer?.files?.[0]);
        }}
      >
        {uploading ? (
          <>
            <div className="dc-spinner" />
            <h3>Uploading…</h3>
            <p>Your part is on its way to the analysis engine</p>
          </>
        ) : (
          <>
            <div className="dc-drop-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <h3>Drop your STEP file here</h3>
            <p>or click to browse · .step / .stp · up to 50MB</p>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".step,.stp"
          style={{ display: 'none' }}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>

      <div className="dc-upload-options">
        <label>
          Robot cell
          <select value={cellPresetId} onChange={(e) => setCellPresetId(e.target.value)}>
            {presets.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
            <option value={CUSTOM_CELL_ID}>Custom bed size…</option>
          </select>
        </label>
        <label>
          Material
          <select value={material} onChange={(e) => setMaterial(e.target.value)}>
            {materials.map((m) => (
              <option key={m.key} value={m.key}>{m.label}{m.highTemp ? ' (high-temp)' : ''}</option>
            ))}
          </select>
        </label>
        <label>
          Print mode
          <select value={printMode} onChange={(e) => setPrintMode(e.target.value)}>
            {printModes.map((m) => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </select>
        </label>
        <label>
          Main load direction
          <select value={loadDirection} onChange={(e) => setLoadDirection(e.target.value)}>
            <option value="none">Not sure / not critical</option>
            <option value="x">Along X (of the CAD model)</option>
            <option value="y">Along Y (of the CAD model)</option>
            <option value="z">Along Z (of the CAD model)</option>
          </select>
        </label>
        {(printMode === 'shell' || printMode === 'auto') && (
          <>
            <label>
              Shell beads
              <select value={perimeterBeads} onChange={(e) => setPerimeterBeads(Number(e.target.value))}>
                {[1, 2, 3, 4].map((n) => (
                  <option key={n} value={n}>{n} bead{n > 1 ? 's' : ''}</option>
                ))}
              </select>
            </label>
            <label>
              Infill
              <select value={infillPct} onChange={(e) => setInfillPct(Number(e.target.value))}>
                {[10, 15, 20, 25, 30, 35, 40, 45, 50].map((n) => (
                  <option key={n} value={n}>{n}%</option>
                ))}
              </select>
            </label>
          </>
        )}
      </div>

      {cellPresetId === CUSTOM_CELL_ID && (
        <div className="dc-custom-cell">
          {['x', 'y', 'z'].map((axis) => (
            <label key={axis}>
              {axis.toUpperCase()} (mm)
              <input
                type="number"
                min={CUSTOM_MM_MIN}
                max={CUSTOM_MM_MAX}
                value={customMm[axis]}
                onChange={(e) => setCustomMm({ ...customMm, [axis]: e.target.value })}
              />
            </label>
          ))}
        </div>
      )}

      <Link className="dc-topic-card" to="/design-check/guide/009-interlayer-temperature">
        <span>Design guide</span>
        <strong>Interlayer temperature and bond strength</strong>
        <p>
          Layers bond when the previous bead stays inside a temperature window for that material.
          Too cold and the weld fails. Too hot and the bead sags.
        </p>
      </Link>

      {jobs.length > 0 && (
        <div className="dc-jobs">
          <h2>Your analyses</h2>
          {jobs.map((job) => (
            <div key={job.id} className="dc-job-row" onClick={() => navigate(`/design-check/analysis/${job.id}`)}>
              <span className="dc-job-name">{job.fileName}</span>
              {job.summary && <span className={`dc-pill ${job.summary.overall}`}>{job.summary.overall}</span>}
              <span className={`dc-pill ${job.status}`}>{statusLabel[job.status] || job.status}</span>
              <span className="dc-job-meta">{new Date(job.createdAt).toLocaleDateString()}</span>
              <button
                className="dc-job-delete"
                title="Delete this analysis"
                onClick={(e) => deleteJob(e, job)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </DesignCheckLayout>
  );
}

export default DesignCheckHome;
